// Best-effort gender of a given name, used only to pick a default coach.
// Unknown, ambiguous, and non-Latin names return null. Nothing is stored.

export type GuessedGender = 'female' | 'male'

const words = (list: string) => new Set(list.split(/\s+/).filter(Boolean))

// Unisex, or gendered opposite ways across the languages we ship.
const AMBIGUOUS = words(`
  addison alex alexis ali angel andrea ariel ashton avery bailey blake cameron
  carey casey charlie charley chris claude camille dakota dale dana darcy guadalupe
  devon dominique drew eden elliot elliott emery finley fran frankie harley harper
  hayden hunter jackie jamie jan jean jesse jessie joan jody jordan kai kelly
  kendall kennedy kim laurie laurence lee leslie lindsay lindsey logan london
  madison marion marley micah morgan noel nur noor nour parker pat payton peyton
  phoenix quinn rae regan remy rene reese river robin rowan sage sam sandy sasha
  shannon shelby sidney simone skylar skyler stacey stacy stevie sydney tatum
  taylor terry tracy val whitney yael yuki
`)

const FEMALE = words(`
  abigail ada adela adele adeline adriana agatha agathe agnes aisha alejandra
  alessandra alexa alexandra alice alicia alison alyssa amanda amber amelia
  amelie amparo amy ana anastasia angela angelica anita ann anna annabelle anne
  annette annie antonia april aria ariana ashley audrey aurelie aurora barbara
  beatrice beatriz bella bernadette beth betty beverly bianca blanche blanca
  bonnie brenda brianna bridget brigitte britney brooke caitlin camila camilla
  candace carla carmen carol carolina caroline carolyn cassandra catalina
  catarina catherine cathy cecile cecilia celine celeste chantal charlotte
  chelsea cheryl chloe christina christine cindy claire clara claudia clemence
  colette concepcion consuelo corinne courtney cristina crystal cynthia daisy
  danielle daphne deborah debra delphine denise diana diane dolores donna
  dorothy eileen elaine eleanor elena elisa elisabeth elise eliza elizabeth
  ella ellen ellie elodie emily emma erica erin esperanza esther estefania
  eugenia eva evelyn faith fatima fiona florence francesca frances
  francisca francoise freya gabriela gabrielle gemma genevieve georgia gianna
  gina giselle gloria grace greta gwen gwendolyn hailey hannah
  hazel heather helen helena helene holly hope ida imogen ines ingrid irene
  iris isabel isabela isabella isabelle ivy jacqueline jade jane janelle janet
  janice jasmine jeanne jeanette jenna jennifer jenny jessica jill joana joanna
  jocelyn josephine josefa josefina joy joyce juana judith julia juliana julie
  juliet juliette june justine karen kate katherine kathleen kathryn kathy
  katie kayla keira kendra kimberly kirsten kristen kristin kristina laetitia
  larissa laura laure lauren lea leah leila lena leona leticia lila lilian
  lillian lily linda lisa lizzie lola lorraine louise luana lucia luciana lucie
  lucy luisa luiza luna luz lydia lynn mabel madeleine madeline maggie maisie
  manon manuela mara margaret margaux marguerite maria mariah marian marie
  mariam marilyn marina marisa marjorie martha mary maryam matilda maureen
  maya megan meg mei melanie melissa melody mercedes meredith mia michelle
  mildred millie miranda miriam molly monica monique montserrat nadia nancy
  naomi natalia natalie natasha nathalie nicole nina noelle nora norma nuria
  oceane odette olivia ophelia paloma pamela patricia patty paula pauline
  pearl peggy penelope penny phoebe pilar piper polly poppy priscila priscilla
  rachel raquel rebecca regina renata renee rita rocio rosa rose rosemary
  rosie ruby ruth sabrina sadie sally samantha sandra sara sarah savannah
  scarlett selena serena sharon sheila shirley sienna silvia sofia
  soledad solene sophia sophie stella stephanie sue susan susanna suzanne
  sylvia sylvie tabitha tamara tanya tara tatiana teresa tess tessa
  thea theresa therese tiffany tina ursula valentina valeria valerie vanessa
  veronica veronique victoria viola violet virginia virginie vivian wendy
  willa willow winifred yasmin yasmine yolanda yvette yvonne zara zainab zoe
  zoey
`)

const MALE = words(`
  aaron adam adrian adrien afonso ahmed ahmad alain alan albert alejandro
  alexandre alfred alonso alvaro andre andres andrew andy antoine antonio
  arnaud arthur austin baptiste barry ben benjamin benoit bernard bill billy
  bob bobby brad bradley brandon brendan brent brett brian bruno bruce
  bryan caio caleb calvin carl carlos carter cedric cesar caesar charles chase
  christian christophe christopher clarence clark clayton clement clifford
  clinton cody colin connor craig curtis damian damien dan daniel danny darren
  dave david dean denis dennis derek derrick diego didier dominic domingo
  donald douglas duncan dustin dylan earl eddie edgar eduardo edward edwin
  eli elias elijah emile enrique eric ernest esteban ethan eugene evan everett
  fabien fabio fabrice felipe felix fernando finn floyd florian francis frank
  franklin francois fred frederick frederic gabriel gael garrett gary gavin
  george gerald gerard gerardo germain ghislain gilbert gilles glen glenn
  gordon graham grant greg gregory guilherme guillaume gustavo guy hank harold
  harry harvey hassan hector henri henrique henry herbert herve howard hugh
  hugo hussein ian ibrahim ignacio isaac isaiah ivan jack jackson jacob jake
  james jared jason javier jay jeff jeffrey jeremy jerome jerry jesus jim
  jimmy joe joel joao john johnny jon jonathan jorge jose joseph josh joshua juan
  julian julio justin karl karim keith ken kenneth kevin kirk kurt kyle lance
  larry laurent lawrence leo leon leonard levi lewis liam lincoln lloyd louis
  loic luc lucas ludovic luis luke malcolm manuel marc marcel marcelo marco
  marcos marcus mario mark marshall martin marvin mason mateo mateus mathew
  matthew maurice max maximilian maxime maxwell melvin michael micheal michel
  miguel mike miles mitchell morris muhammad mohammed mohamed mohammad nathan
  nathaniel neil nelson nicholas nick nicolas nigel noah norman olivier oliver
  omar oscar owen pablo pascal patrice patrick paul paulo pedro perry peter
  philip philippe phillip pierre quentin rafael ralph ramon randall randy raul
  ray raymond reed reginald remi ricardo richard rick ricky rob robert roberto
  rodrigo roger roland ron ronald ross roy ruben russell ryan salvador samir
  samuel santiago scott sean sebastian sebastien serge sergio seth shane shawn
  simon spencer stanley stephen steve steven stewart stuart sylvain ted
  terrence theodore thiago thierry thomas tiago tim timothy todd tom tommy tony
  travis trevor tristan troy tyler tyrone victor vincent virgil vitor wade
  wallace walter warren wayne wesley will william willie wyatt xavier yann
  yves yusuf youssef zachary zach
`)

function overlaps(a: Set<string>, b: Set<string>): string[] {
  const hit: string[] = []
  for (const name of a) if (b.has(name)) hit.push(name)
  return hit
}

const clash = [
  ...overlaps(FEMALE, MALE),
  ...overlaps(FEMALE, AMBIGUOUS),
  ...overlaps(MALE, AMBIGUOUS),
]
if (clash.length > 0) throw new Error(`given name lists overlap: ${clash.join(', ')}`)

// First whitespace token, then the piece before . or _. Accents fold away.
function givenToken(name: string | null | undefined): string {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? ''
  const piece = first.split(/[._]/)[0] ?? ''
  return piece
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z-]/g, '')
}

function lookup(token: string): GuessedGender | 'ambiguous' | null {
  if (!token) return null
  if (AMBIGUOUS.has(token)) return 'ambiguous'
  if (FEMALE.has(token)) return 'female'
  if (MALE.has(token)) return 'male'
  return null
}

export function guessGender(name: string | null | undefined): GuessedGender | null {
  const token = givenToken(name)
  if (!token) return null
  const hit = lookup(token)
  if (hit === 'ambiguous') return null
  if (hit) return hit
  // Mary-Jane → Mary. Jean-Luc stays unknown because Jean is ambiguous.
  const head = token.split('-')[0] ?? ''
  if (!head || head === token) return null
  const part = lookup(head)
  return part === 'ambiguous' || !part ? null : part
}

// Empty names stay unknown. Callers pass the account name, never the mailbox.
export function nameForGenderGuess(name: string | null | undefined): string | null {
  const trimmed = name?.trim() ?? ''
  return trimmed || null
}
